import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/common/components/ui/input';
import { useDebouncer } from '@/common/hooks/useDebouncer';
import { cn } from '@/lib/utils';

interface ColumnFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
}

export const ColumnFilter = ({
  value,
  onChange,
  placeholder,
  debounceMs = 500,
}: ColumnFilterProps) => {
  const [localValue, setLocalValue] = useState(value);
  const debouncedOnChange = useDebouncer(onChange, debounceMs);
  const { t } = useTranslation();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <div className="relative flex items-center gap-1 w-56">
      <div className="relative flex-1">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
        <Input
          autoFocus
          type="text"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder={placeholder}
          className={cn('h-7 pl-7 pr-6 text-xxs', localValue && 'pr-7')}
        />
        {localValue && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            aria-label={t('billing.filter.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};
